import { getLabel, useMount } from '@apia/util';
import {
  isAssistantEnabled,
  useIsAssistantEnabled,
} from './util/useIsAssistantEnabled';
import { validationsStore } from '@apia/validations';
import { customEvents } from '../../../../../utils/dom';
import store from '../../../../../store';
import { TFieldData } from './types';
import { getFormsAssistantConfiguration } from './getFormsAssistantConfiguration';
import { canDocument } from './canDocument';
import { getFormsAssistantFormName } from './getFormsAssistantFormName';
import { ChatMessage, FloatingChatController, TMessageType } from '@apia/ai';
import { TMenuItem } from '@apia/components';
import { runPromptForms } from './util/forms/runPromptForms';
import ApiaApi, { makeApiaUrl } from '../../../../../api/apiaApi/apiaApi';
import { resolveResponseResult } from './util/forms/fillInformation/resolveResponseResult';
import { Box, spacing } from '@apia/theme';
import { generateUserManual } from './util/userManual/generateUserManual';

function isMobile() {
  return /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
}

export const executionFloatingChatController = new FloatingChatController({
  props: {
    feedbackForm: 'phrase',
    isWindowOpen: false,
  },
  onMessageSubmit: async (props) => {
    executionFloatingChatController.addMessage(props.message);
    if (props && props.message) {
      try {
        await runPromptForms({
          isPhone: isMobile(),
          prompt: props.message.message as string,
          files: props.message.attachments,
        });
      } catch (e) {
        console.error(e);
      }
    }
  },
  onClearHistory: () => {},
  additionalOptions: [
    ...[
      window.IS_TASK_DOCUMENTATION_SERVICE_ENABLED
        ? {
            key: 'document',
            children: getLabel('btnAiGenerateManual').text,
            title: getLabel('btnAiGenerateManual').title,
            onClick: () => {
              executionFloatingChatController.state.isLoading = true;
              generateUserManual()
                .finally(() => {
                  executionFloatingChatController.state.isLoading = false;
                })
                .catch((error) => {
                  throw new Error(error);
                });
            },
          }
        : null,
      window.IS_FORM_AUTO_COMPLETE_ENABLED
        ? {
            children: getLabel('btnAutoCompleteAiChat').text,
            key: 'autocomplete',
            onClick: () => {
              executionFloatingChatController.state.isLoading = true;
              const prompt =
                'Genera datos de prueba para todos los campos de todos los formularios.';
              executionFloatingChatController.addMessage(
                new ChatMessage(prompt, 'user'),
              );
              void runPromptForms({
                isPhone: isMobile(),
                prompt: prompt,
              })
                .finally(() => {
                  executionFloatingChatController.state.isLoading = false;
                })
                .catch((error) => {
                  executionFloatingChatController.state.isLoading = false;
                  throw new Error(error);
                });
            },
          }
        : null,
    ].filter((item) => item != null),
  ] as TMenuItem[],
  classNames: { history: 'assistantHistory' },
});

declare global {
  interface Window {
    IS_FIRST_TASK: boolean;
    ENTITY_EXECUTION?: boolean;
  }
}

export const HARDCODED_PARAM: TFieldData = {
  title: 'HARDCODED_PARAM',
  type: 'S',
  description: 'THIS VALUE MUST BE FILLED WITH true',
  enum: ['true'],
};

export const needsAutoComplete = window.IS_FORM_AUTO_COMPLETE_ENABLED;
let hasInited = false;

function initAssistantConfiguration() {
  if (hasInited) return;
  hasInited = true;
  validationsStore.registerForm(getFormsAssistantFormName(), {
    avoidFieldsOverride: true,
    persistToSessionStorage: false,
    initialValues: {
      ...('txtComment' in
        (store.getState().previousValidationSlice.forms.executionData?.values ??
          {}) &&
      getFormsAssistantConfiguration().OBSERVATIONS &&
      (isAssistantEnabled.getState('isEnabled') || needsAutoComplete)
        ? {
            [getFormsAssistantConfiguration().OBSERVATIONS.name]: true,
          }
        : null),
      ...(needsAutoComplete &&
      getFormsAssistantConfiguration().ALLOW_MODIFY_AUTOCOMPLETE
        ? {
            [getFormsAssistantConfiguration().ALLOW_MODIFY_AUTOCOMPLETE.name]:
              false,
          }
        : {}),
      ...(canDocument() &&
      getFormsAssistantConfiguration().FORCE_DOCUMENT_PROCESS_FLOW
        ? {
            [getFormsAssistantConfiguration().FORCE_DOCUMENT_PROCESS_FLOW.name]:
              false,
          }
        : {}),
    },
  });
  const checkData = async () => {
    try {
      executionFloatingChatController.state.isLoading = true;

      const result = await ApiaApi.get<{ json: string }>(
        makeApiaUrl({
          action: 'checkAssistantResult',
        }),
      );
      if (result?.data) {
        const form = JSON.parse(result?.data.json) as {
          message: string;
          filledFormsMap: Record<string, Record<string, string>>;
        };
        if (form.filledFormsMap) {
          await resolveResponseResult(result.data, isMobile());
        }
      }
    } finally {
      executionFloatingChatController.state.isLoading = false;
    }
  };
  if (window.location.href.includes('fromAssistantPanel=true')) {
    executionFloatingChatController.open();
    checkData();
  }
}

const FormsAssistant = () => {
  const { isEnabled } = useIsAssistantEnabled();

  useMount(() => {
    window.addEventListener(
      customEvents.formsReady,
      initAssistantConfiguration,
    );
    executionFloatingChatController.addMessage(
      new ChatMessage(
        getLabel('lblAiFormsAssistantWelcomeMessage').text,
        'information' as TMessageType,
      ),
    );

    document.addEventListener('floatingAIPrompt', close);

    return () => {
      document.removeEventListener('floatingAIPrompt', close);
    };
  });

  if (
    !isEnabled &&
    !window.IS_TASK_DOCUMENTATION_SERVICE_ENABLED &&
    !needsAutoComplete
  )
    return null;

  return (
    <Box
      className="floatingChatButton"
      sx={{ position: 'fixed', right: spacing(3), bottom: spacing(3) }}
    >
      <executionFloatingChatController.Window />
    </Box>
  );
};

export default FormsAssistant;
